import { Tooltip } from "@mui/material";
import { Button, TextLink } from "@vds/buttons";
import { Loader } from "@vds/loaders";
import { Body, Title } from "@vds/typography";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import BreadcrumbsContainer from "../../global/components/BreadcrumbsContainer";
import { transformPolygonData } from "../../global/utility/roiUtils";
import DisplayCanvas from "../../polygonLib/displayPolygonLib/displayCanvas";
import { fetchAssociatedStreamsRequest, fetchAssociatedStreamsSuccess } from "../../store/actions/ea-actions";
import "./associatedStreams.scss";
import { getBreadcrumbConfig, NO_ASSOCIATED_STREAMS_MESSAGE, NO_DEPLOYMENTS_MESSAGE } from "./constants/constants";
import { fetchAssociatedStreams } from "./services/services";
import { DUMMY_APPEARANCE } from "./dummyAppearance";
import { LICENSE_NOTIFICATION_MESSAGES, LICENSE_NOTIFICATION_TYPES, LICENSE_TYPES, LICENSE_WARNINGS, getActiveLicenseSku, ROI_DELETE_STATUS, ROI_NOTIFICATION_TYPES, UNSUBSCRIBE_FLOW_TEXTS, ORIGIN_PAGES } from "../../global/constants/licensesConstants";
import ErrorTemplate from "../../global/components/errorTemplate/ErrorTemplate";
import StreamCarousel from "../../global/components/imageCarousel/CarouselComponent";
import { Icon } from "@vds/icons";
import styled from "styled-components";
import { BANNER_TYPES, DELETE_GROUP_MODAL_CONSTANTS, NO_PERMISSION, SEVERITY_LEVELS } from "../../global/constants/constants";
import { resetRoi, setStatistic, showNotificationBanner, deleteRoiRequest, unsubscribeObjectBlurringRoiRequest, successDeleteRoi, clearAdvancedConfigAppearances, successTagRoiResponse, getAllRoiListWithCvApps, patchWidgetConfig } from "../../store/actions";
import { CVAPP_OBJECTBLUR, CVAPP_CAMERAADJUSTMENT } from "../../global/constants/cvappConstants";
import { deploymentstatusList, isRoiWithDeployedStatus } from "../../global/utility/statisticsUtil";
import { hasPermission } from "../../global/utility/permissionUtils";
import { getTagName } from "../../model/reportService";
import { adjustPolygonPoints } from "../../utils";
import { getCompanionObjectById } from "../../global/utility/deviceTableUtils";
import SnapshotComponent from "../CameraOnboarding/SnapshotComponent";
import { getcvAppName } from "../../global/utility/cvAppUtils";
import { ImageDownloadMedia } from "../../model/mediaService";
import { getFovSnapshot, handleApiError } from "../../global/utility/Utils";
import { BADGE_COLORS, ASSOCIATE_STREAMS } from "../../constants/enrolledAppearanceConstants";
import { Text } from "@vds/core";
import { NOTIFICATION_PRIORITIES } from "../Polygon/constants";
import { getAppearances } from "../../api/EnrolledAppearanceServiceManager";
import { DEPLOY_API, DEPLOY_MODAL_MESSAGES, ROI_MESSAGES } from "../../global/constants/roiConstants";
import { NOTIFICATION_TYPES } from "../Settings/constants";
import { updateDeployRoi } from "../../api/RoiServiceManager";
import RoiActionModal from "../../global/components/Modals/RoiActionModal";

const StyledIconsET = styled.div`
  cursor: pointer;
  background: #ffffff;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    transform: scale(1.08);
  }
  
  /* Tooltip Label Box */
  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background-color: #1e1e1e;
    color: #ffffff;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    z-index: 100;
  }
`;

const AssociatedStreams = () => {
  const location = useLocation();
  const { id } = location.state || {};
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [appearance, setAppearance] = useState(
   location.state?.appearance
  );
  const [roiList, setRoiList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [floorImage, setFloorImage] = useState("");
  const [cvAppName, setCvAppName] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState({
    isOpen: false,
    selectedRoi: null,
  });
  const [deletingDeploymentId, setDeletingDeploymentId] = useState(null);

  const [showReDeployModal, setShowReDeployModal] = useState({
    isOpen: false,
    selectedRoi: null,
  });
  const [reDeployingDeploymentId, setReDeployingDeploymentId] = useState(null);

  const snapshotsByFovId = useRef({});
  const deletedRoiRef = useRef(null);
  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession || {});
  const roiListFromRedux = useSelector((state) => state.deviceReducer.roiList);
  const { associatedStreams, associatedStreamsLoading } = useSelector((state) => state.enrolledAppearanceReducer);

  const fovList = useSelector((state) => state.deviceReducer.fovList);
  const cameraList = useSelector((state) => state.deviceReducer.cameraList);
  const companionDeviceList = useSelector((state) => state.deviceReducer.companionDeviceList);
  const licenses = useSelector((state) => state.licenseReducer?.licenses || []);
  const roiDeleteResponse = useSelector((state) => state.polygonRoiReducer.deleteRes);
  const tagIdList = useSelector((state) => state.sitesReducer.tagIdList);
  const cvAppList = useSelector((state) => state.cvAppReducer.cvAppList) || [];
  const widgetConfig = useSelector((state) => state.dashboardReducer?.widgetConfig) || [];
  const roiListItem = useSelector((state) =>
    state.polygonRoiReducer?.roiListWithCvapps?.filter(isRoiWithDeployedStatus)
  );
  const roiListData = roiListItem || [];
  const imageSize = { width: 214, height: 128 };

  // Guarded Load Function with default empty array fallbacks
  const loadAssociatedStreams = async (currentAppearance = appearance) => {
    
    if (!currentAppearance) {
      setIsLoading(false);
      return;
    }

    try {
      const enrichedCameras = await fetchAssociatedStreams(
        currentAppearance,
        fovList || [],
        roiListFromRedux || [],
        cameraList || [],
        licenses || []
      );

      if (enrichedCameras === null) {
        setRoiList([]);
        setIsLoading(false);
        return;
      }
      setRoiList(enrichedCameras);
    } catch (error) {
      console.log("Error fetching streams:", error);
      setRoiList([]);
      setIsLoading(false);
    }
  };

  const showLicenseSuspendedBanner = () => {
    const notificationInfo = {
      type: BANNER_TYPES.INFORMATION,
      priority: BANNER_TYPES.WARNING,
      showNotification: true,
      title: LICENSE_NOTIFICATION_TYPES.LICENSE_SUSPENDED,
      subtitle: LICENSE_NOTIFICATION_MESSAGES.LICENSE_SUSPENDED_MESSAGE,
    };
    dispatch(showNotificationBanner(notificationInfo));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: NO_PERMISSION,
      priority: SEVERITY_LEVELS.CRITICAL,
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  const renderCameraHeader = (label) => (
    <div className="camera-header">
      <Tooltip
        placement="top"
        title={
          <div className="camera-header-tooltip">
            <Body size="small" viewport="desktop">
              {label}
            </Body>
          </div>
        }
        arrow
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: "white",
              border: "1px solid black",
              "& .MuiTooltip-arrow": { backgroundColor: "white" }
            }
          }
        }}
      >
        <sup className="camera-header-sup">
          <Text type="standAlone" size="medium" className="camera-header-link">
            <div className="camera-header-label">{label}</div>
          </Text>
        </sup>
      </Tooltip>
    </div>
  );

  const getTags = (tagIds) => tagIds.map((tagId) => getTagName(tagId, tagIdList));

  const handleEditRoi = (e, selectedRoi) => {
    const licenseStatus = selectedRoi?.licensesinfo?.find((lic) => lic?.licensetype === LICENSE_TYPES.STANDARD)?.status?.toLowerCase() ?? "";
    const isLicenseSuspended = licenseStatus?.toLowerCase() === 'suspended';

    e.stopPropagation();
    if (isLicenseSuspended) {
      showLicenseSuspendedBanner();
      return;
    }
    if (!hasPermission("Apps", "Edit")) {
      showPermissionsBanner();
      return;
    }

    dispatch(resetRoi());

    const appearanceDeployment = appearance?.deployments?.find(
      (deployment) => deployment.deviceid === selectedRoi?.deploymentDeviceId
    );
    const deployParameters = appearanceDeployment?.config?.["deploy-parameters"];
    const polygonPoints = transformPolygonData(selectedRoi?.customdata);
    const frame = selectedRoi?.customdata;
    const maxX = frame?.polygon?.frame?.maxX
      ? frame?.polygon?.frame?.maxX
      : frame?.polygon?.polygon?.frame?.maxX;
    const maxY = frame?.polygon?.frame?.maxY
      ? frame?.polygon?.frame?.maxY
      : frame?.polygon?.polygon?.frame?.maxY;

    if (deployParameters?.zone1 || deployParameters?.zone2) {
      const groupName = `${selectedRoi?.parentCamera?.name || selectedRoi?.name}-${appearance?.name}`;
      const zones = [
        {
          groupName,
          statisticData: deployParameters.zone1?.length > 0 ? getTags(deployParameters.zone1) : ["Unused"],
          tags: deployParameters.zone1?.length > 0 ? deployParameters.zone1 : ["-1"],
          eeAppStatus: "Zone1",
        },
        {
          groupName,
          statisticData: deployParameters.zone2?.length > 0 ? getTags(deployParameters.zone2) : ["Unused"],
          tags: deployParameters.zone2?.length > 0 ? deployParameters.zone2 : ["-1"],
          eeAppStatus: "Zone2",
        },
      ];
      dispatch(setStatistic(zones));
    }

    const cameraData = selectedRoi?.parentCamera;
    const streamData = fovList?.filter((fov) => fov.parentdeviceid === selectedRoi?.parentdeviceid) || [];

    const roiDevice = roiListFromRedux?.find((roi) => roi.id === selectedRoi?.deploymentDeviceId);
    const cvAppName = getcvAppName(cvAppList, appearanceDeployment?.cvappid);
    const groupName = `ROI1-${selectedRoi?.name}-${cvAppName}`;
    setCvAppName(cvAppName);

    const roiItem = {
      name: groupName,
      deployments: appearance?.deployments,
      groupName,
      cvAppName,
      points: adjustPolygonPoints([maxX, maxY], polygonPoints, [832, 532]),
      originalPoints: polygonPoints && polygonPoints.length >= 3 ? polygonPoints : [],
      roiId: selectedRoi?.deploymentDeviceId,
      frame,
    };

    const cvappItem = {
      title: selectedRoi?.name,
      value: cvAppName,
      cvappid: appearanceDeployment?.cvappid,
      roiId: selectedRoi?.deploymentDeviceId,
      tagids: roiDevice?.tagids,
      groupName,
      deployments: appearance?.deployments,
    };

    const selectedState = {
      cvapps: [cvappItem],
      roiUpdateData: [roiItem],
      FOVId: selectedRoi?.id,
      cameraId: cameraData?.id,
      companionId: cameraData?.parentdeviceid,
      action: "update",
      cameraData,
      streamData,
      FOVSnapshot: selectedRoi?.imageUrl,
      fieldOfViews: streamData,
      FOVName: selectedRoi?.name,
      location: ASSOCIATE_STREAMS,
      cameraname: cameraData?.name,
      cvappid: appearanceDeployment?.cvappid,
      tagids: roiDevice?.tagids,
      deployParameters: deployParameters || [],
      deploymentId: appearanceDeployment?.id,
      sku: getActiveLicenseSku(selectedRoi?.licenses),
      roiList: [roiItem],
      siteSelected: cameraData?.siteids,
      originPage: ASSOCIATE_STREAMS,
      appearanceName: appearance?.name,
      appearance
    };
    navigate("/polygon", { state: selectedState });
  };

  const handleDeleteRoi = async (e, kObj) => {
    if (!hasPermission("Apps", "Delete")) {
      showPermissionsBanner();
      return;
    }

    const appearanceDeployment = appearance?.deployments?.find(
      (dep) => dep.deviceid === kObj?.deploymentDeviceId
    );
    const cvAppName = getcvAppName(cvAppList, appearanceDeployment?.cvappid);

    setCvAppName(cvAppName);
    setShowDeleteModal({
      isOpen: true,
      selectedRoi: kObj,
    });

    const cachedUrl = snapshotsByFovId.current[kObj?.id];
    if (cachedUrl) {
      setFloorImage(cachedUrl);
    } else {
      const imgUrl = await ImageDownloadMedia(
        getFovSnapshot(fovList, kObj?.id),
        activeSession,
        "",
        "snapshot"
      );
      snapshotsByFovId.current[kObj?.id] = imgUrl;
      setFloorImage(imgUrl);
    }
  };

  const handleCloseModal = () => {
    setShowDeleteModal({ isOpen: false, selectedRoi: null });
  };

  const handleOpenReDeployModal = (e, kObj) => {
    const appearanceDeployment = appearance?.deployments?.find(
      (dep) => dep.deviceid === kObj?.deploymentDeviceId
    );
    const cvAppName = getcvAppName(cvAppList, appearanceDeployment?.cvappid);
    setCvAppName(cvAppName);

    setShowReDeployModal({
      isOpen: true,
      selectedRoi: kObj,
    });
  };

  const fetchLatestAppearanceData = async () => {
    if (!activeSession?.accountId) {
      await loadAssociatedStreams(appearance);
      return;
    }

    try {
      const response = await getAppearances({}, activeSession);

      const appearancesList = Array.isArray(response)
        ? response
        : response?.data || [];
      const targetId = appearance?.id || id;
      const freshAppearance = appearancesList.find((item) => item.id === targetId);
      if (freshAppearance) {
        setAppearance(freshAppearance);
        await loadAssociatedStreams(freshAppearance);
      } else {
        await loadAssociatedStreams(appearance);
      }
    } catch (error) {
      handleApiError(NOTIFICATION_TYPES.WARNING, "Failed to fetch the latest appearance:", error);
      await loadAssociatedStreams(appearance);
    }
  };

  const handleConfirmReDeploy = async () => {
    dispatch(successTagRoiResponse([]));
    dispatch(clearAdvancedConfigAppearances());
    const selectedItem = showReDeployModal.selectedRoi;

    if (!selectedItem?.deploymentId && !selectedItem?.deploymentDeviceId) {
      handleApiError(NOTIFICATION_TYPES.WARNING, "Re-deploy failed, please refresh the page or try again later.");
      setShowReDeployModal({ isOpen: false, selectedRoi: null });
      return;
    }

    const targetDeploymentId = selectedItem.deploymentId || selectedItem.deploymentDeviceId;

    const targetDeployment = appearance?.deployments?.find(
      (dep) => dep.id === targetDeploymentId || dep.deviceid === selectedItem.deploymentDeviceId
    );

    const existingConfig = targetDeployment?.config || {};

    const payload = {
      accountname: appearance?.billingAccountId || activeSession?.accountId,
      resourceidentifier: {
        id: targetDeploymentId,
      },
      deployment: {
        config: {
          "deploy-parameters": existingConfig["deploy-parameters"] || {},
          "alert-parameters": existingConfig["alert-parameters"] || {},
        },
      },
    };

    setShowReDeployModal({ isOpen: false, selectedRoi: null });
    setReDeployingDeploymentId(targetDeploymentId);

    try {
      const deployResp = await updateDeployRoi(payload, activeSession);
      if (deployResp && Object.keys(deployResp).length && !deployResp?.hasOwnProperty("error")) {
        dispatch(getAllRoiListWithCvApps());
        const notificationInfo = {
          type: "Deploy cvapp success",
          priority: NOTIFICATION_PRIORITIES.SUCCESS,
          data: {
            title: DEPLOY_API.SUCCESS,
            subTitle: DEPLOY_API.SUCESS_MESSAGE,
          },
          showNotification: true,
        };
        dispatch(showNotificationBanner(notificationInfo));
      }
    } catch (error) {
      const notificationInfo = {
        type: "Deploy cvapp failed",
        priority: NOTIFICATION_PRIORITIES.CRITICAL,
        data: {
          title: DEPLOY_API.FAILED,
          subTitle: DEPLOY_API.FAILED_MESSAGE,
        },
        showNotification: true,
      };
      dispatch(showNotificationBanner(notificationInfo));
    } finally {
      await fetchLatestAppearanceData();
      setReDeployingDeploymentId(null);
    }
  };

  const confirmDeleteRoi = () => {
    const selectedRoi = showDeleteModal.selectedRoi;
    if (!selectedRoi?.deploymentDeviceId) {
      handleApiError(NOTIFICATION_TYPES.WARNING, "Failed to delete roi, Please refresh the page or try again.");
      setShowDeleteModal({ isOpen: false, selectedRoi: null });
      return;
    }
    const appearanceDeployment = appearance?.deployments?.find(
      (dep) => dep.deviceid === selectedRoi?.deploymentDeviceId
    );
    if (!appearanceDeployment?.cvappid) {
      handleApiError(NOTIFICATION_TYPES.WARNING, "Failed to delete roi, Please refresh the page or try again.");
      setShowDeleteModal({ isOpen: false, selectedRoi: null });
      return;
    }
    deletedRoiRef.current = selectedRoi;
    const roiDevice = roiListFromRedux?.find((roi) => roi.id === selectedRoi?.deploymentDeviceId);
    const roiId = selectedRoi?.deploymentDeviceId;
    const cvappId = appearanceDeployment?.cvappid;
    const uniqueDeploymentId = selectedRoi?.deploymentId || roiId;
    setDeletingDeploymentId(uniqueDeploymentId);

    const payload = {
      accountname: activeSession?.accountId,
      resourceidentifier: {
        deviceid: roiId,
      },
      appid: cvappId,
    };

    const cvAppName = getcvAppName(cvAppList, cvappId);
    if (cvAppName === CVAPP_OBJECTBLUR || cvAppName === CVAPP_CAMERAADJUSTMENT) {
      const deploymentIds = roiDevice?.deployments?.filter(
        (d) => d.appid === cvappId && deploymentstatusList.includes(d.status)
      );
      if (deploymentIds && deploymentIds.length > 0) {
        deploymentIds.forEach((item) => {
          dispatch(unsubscribeObjectBlurringRoiRequest({ ...payload, deploymentIds: item?.id }, cvAppName));
        });
      }
    } else {
      dispatch(deleteRoiRequest(payload));
    }
    setShowDeleteModal({ isOpen: false, selectedRoi: null });
  };

  const updateSnapshot = (fovId, url) => {
    if (!url) return;
    snapshotsByFovId.current[fovId] = url;
    if (fovId === showDeleteModal.selectedRoi?.id) {
      setFloorImage(url);
    }
  };

  const getDeploymentStatusBadge = (status) => {
    const normalizedStatus = (status || "").toLowerCase();
    if (normalizedStatus === "deployed") {
      return {
        badgeLabel: "Deployed",
        badgeBg: BADGE_COLORS.BG_ACTIVE,
        badgeTextColor: BADGE_COLORS.TEXT_WHITE,
      };
    }

    return {
      badgeLabel: "Failed",
      badgeBg: "#FF8027",
      badgeTextColor: BADGE_COLORS.TEXT_WHITE,
    };
  };

  const renderPolygonCard = (kObj) => {
    const originalSize = kObj?.customdata?.polygon?.polygon?.frame
      ? [kObj?.customdata, kObj?.customdata] : "";

    const cardId = kObj.id || kObj.name;
    const companionDeviceData = getCompanionObjectById(kObj.companionid, companionDeviceList);
    const deploymentBadge = getDeploymentStatusBadge(kObj.deploymentStatus);
    const status = kObj.deploymentStatus;
    const formattedStatus = `Appearance status: ${status}`

    const isDeletingThisRoi =
      deletingDeploymentId &&
      (deletingDeploymentId === kObj.deploymentId ||
        deletingDeploymentId === kObj.deploymentDeviceId);

    const isReDeployingThisRoi =
      reDeployingDeploymentId &&
      (reDeployingDeploymentId === kObj.deploymentId ||
        reDeployingDeploymentId === kObj.deploymentDeviceId);

    const isCardLoading = isDeletingThisRoi || isReDeployingThisRoi;

    return (
      <div className="polygon-card" key={`polygon-${cardId}`}>
        <div className="polygon-card-header-row">
          <div className="stream-name-wrapper">
            {renderCameraHeader(kObj.name)}
          </div>

          <Tooltip title={formattedStatus} placement="top">
            {kObj.deploymentStatus && (
              <span
                className="deployment-status-badge"
                title={deploymentBadge.tooltipText}
                style={{
                  backgroundColor: deploymentBadge.badgeBg,
                  color: deploymentBadge.badgeTextColor,
                }}
              >
                {deploymentBadge.badgeLabel}
              </span>
            )}
          </Tooltip>
        </div>

        {kObj.embeddingVersion !== null && kObj.embeddingVersion !== undefined && (
          <span className="embedding-version-text">
            Appearance version: {kObj.embeddingVersion}
          </span>
        )}

        <div className="polygon-card-canvas-container">
          {isCardLoading && (
            <div className="card-image-loader-overlay">
              <Loader active={true} fullscreen={false} />
            </div>
          )}
          <div className="polygon-card-canvas">
            <SnapshotComponent
              editFlow={true}
              fovObj={{ fovId: kObj.id }}
              selectedCameraQR={kObj.parentCamera?.qrcode}
              companionSelected={companionDeviceData?.qrcode}
              imageSize={imageSize}
              initialPolygons={transformPolygonData(kObj.customdata)}
              originalSize={originalSize}
              component={DisplayCanvas}
              handleChange={(url) => updateSnapshot(kObj.id, url)}
            />
          </div>

          <div className="associate-streams-button-container">
            <StyledIconsET
              data-tooltip="Edit Roi"
              data-testid="roiList_editIcon"
              onClick={(e) => handleEditRoi(e, kObj)}
            >
              <Icon name="edit" size="medium" ariaHidden={true} />
            </StyledIconsET>
            <StyledIconsET
              data-tooltip="Delete Roi"
              data-testid="roiList_trashIcon"
              onClick={(e) => handleDeleteRoi(e, kObj)}
            >
              <Icon name="trash" size="medium" ariaHidden={true} />
            </StyledIconsET>
            {status && status !== "deployed" && (
              <StyledIconsET
                data-tooltip="Re Deploy"
                data-testid="roiList_reDeployIcon"
                onClick={(e) => handleOpenReDeployModal(e, kObj)}
              >
                <Icon name="carryover-data" size="medium" ariaHidden={true} />
              </StyledIconsET>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleNavigateToCameraDetails = (cameraId) => {
    if (!cameraId) {
      console.log(LICENSE_WARNINGS.CAMERA_ID_NOT_PROVIDED);
      return;
    }

    const cameraData = cameraList?.find((camera) => camera.id === cameraId);

    if (cameraData) {
      navigate(`/cameraInfoPage?cameraId=${cameraData?.id}`, {
        state: {
          originPage: "associatedStreams",
          appearance,
          data: []
        }
      });
    } else {
      console.log(LICENSE_WARNINGS.CAMERA_DATA_NOT_FOUND);
    }
  };

  const renderAssociatedStreams = () => (
    <div className="roiBodySection">
      {Array.isArray(associatedStreams) &&
        associatedStreams.length > 0 &&
        associatedStreams.map((m, mIndex) => {
          const siteKey = `${m.siteName}-${mIndex}`;
          const allAreas = [];

          if (Array.isArray(m.roiList) && m.roiList.length > 0) {
            m.roiList
              .filter((group) => Array.isArray(group) && group.length > 0)
              .forEach((group, groupIndex) => {
                const groupedByArea = {};
                group.forEach((roi) => {
                  const areaName = roi.areaName || "Unnamed Area";
                  if (!groupedByArea[areaName]) {
                    groupedByArea[areaName] = [];
                  }
                  groupedByArea[areaName].push(roi);
                });

                Object.keys(groupedByArea).forEach((areaName) => {
                  allAreas.push({
                    areaName,
                    rois: groupedByArea[areaName],
                    key: `area-${groupIndex}-${areaName}`
                  });
                });
              });
          }

          const cameraContainerItems = allAreas.map((area) => (
            <div key={area.key} className="area-container">
              <div className="area-header">
                <Tooltip title={area.rois[0]?.parentCamera?.name || 'Unknown Camera'} placement="top" arrow>
                  <span className="area-header-tooltip-anchor">
                    <Body size="medium">
                      <TextLink
                        disabled={!area.rois[0]?.parentCamera?.name}
                        type="standAlone"
                        size="large"
                        onClick={() => handleNavigateToCameraDetails(area.rois[0]?.parentCamera?.id)}
                      >
                        <span className="area-header-name">
                          {area.rois[0]?.parentCamera?.name || 'Unknown Camera'}
                        </span>
                      </TextLink>
                    </Body>
                  </span>
                </Tooltip>
              </div>
              <div className="roi-cards-container">
                {area.rois.map((kObj) => renderPolygonCard(kObj))}
              </div>
            </div>
          ));

          const totalRoisCount = allAreas?.reduce((sum, area) => sum + (area.rois?.length || 0), 0) || 0;

          return (
            <div className="roiSiteSection" key={siteKey}>
              <span className="site-name">{m.siteName}</span>
              <div className="roiListElements-wrapper">
                <StreamCarousel
                  items={cameraContainerItems}
                  scrollStep={238} gap={16}
                  imagesCount={totalRoisCount}
                />
              </div>
            </div>
          );
        })}
    </div>
  );

  useEffect(() => {
    loadAssociatedStreams();
  }, [fovList, roiListFromRedux, cameraList]);

  useEffect(() => {
    if (!roiList || roiList.length === 0) {
      dispatch(fetchAssociatedStreamsSuccess([]));
      setIsLoading(false);
      return;
    }

    dispatch(fetchAssociatedStreamsRequest({ roiList }));
  }, [roiList, activeSession, dispatch]);

  useEffect(() => {
    setIsLoading(associatedStreamsLoading);
  }, [associatedStreamsLoading]);

  useEffect(() => {
    if (roiDeleteResponse === ROI_DELETE_STATUS.SUCCESS) {
      const notificationInfo = {
        type: ROI_NOTIFICATION_TYPES.ROI_DELETE,
        priority: NOTIFICATION_PRIORITIES.SUCCESS,
        data: {
          title: UNSUBSCRIBE_FLOW_TEXTS.ROI_DELETE_SUCCESS,
          subTitle: "",
        },
        showNotification: true,
      };
      dispatch(showNotificationBanner(notificationInfo));

      // Widget Configuration Cleanup Logic
      try {
        const targetRoi = deletedRoiRef.current;
        const roiData = targetRoi ? [targetRoi] : [];

        if (roiData?.length > 0 && Array.isArray(widgetConfig)) {
          let updatedWidgets = [...widgetConfig];
          let roiSelected = {};
          roiData.forEach((m) => {
            const targetRoiId = m.deploymentDeviceId;
            roiSelected = roiListData.find((f) => f.id === targetRoiId) || m;

            const targetAppId = roiSelected?.deployments?.[0]?.appid ||
              appearance?.deployments?.find((d) => d.deviceid === targetRoiId)?.cvappid;

            updatedWidgets = updatedWidgets.filter((widget) => {
              return !(
                widget?.content?.appName === targetAppId &&
                roiSelected?.tagids?.includes(widget?.content?.location)
              );
            });
          });

          dispatch(patchWidgetConfig({ customdata: { widgetconfig: updatedWidgets } }));
        }
      } catch (error) {
        console.error("Failed to update widget configuration:", error);
      } finally {
        deletedRoiRef.current = null;
      }

      dispatch(getAllRoiListWithCvApps());
      dispatch(successDeleteRoi([]));
      fetchLatestAppearanceData();
      setDeletingDeploymentId(null);
    } else if (roiDeleteResponse === ROI_DELETE_STATUS.ERROR) {
      dispatch(successDeleteRoi([]));
      setDeletingDeploymentId(null);
      deletedRoiRef.current = null;
    }
  }, [roiDeleteResponse]);

  if (isLoading) {
    return <Loader active={true} fullscreen={false}></Loader>;
  }

  return (
    <div className="associated-streams-container">
      <div>
        <BreadcrumbsContainer config={getBreadcrumbConfig(appearance?.name ?? "")} />
        <Title size="large" data-testid="view-deployments-title">
          Deployments
        </Title>
      </div>
      <div>
        <div>
          <Title size="small">Showing deployments for: {appearance?.name ?? "NA"}</Title>
          {associatedStreams && associatedStreams.length > 0 && (
            <Body size="small" color="grey">Appearance version: {appearance?.embeddingVersion ?? "NA"}</Body>
          )}
        </div>
        <div className="streams-content">
          {associatedStreamsLoading ? (
            <Loader active={true} fullscreen={false}></Loader>
          ) : Array.isArray(associatedStreams) && associatedStreams.length > 0 ? (
            renderAssociatedStreams()
          ) : (
            <div className="no-streams-container">
              <ErrorTemplate
                title={NO_ASSOCIATED_STREAMS_MESSAGE}
                description={NO_DEPLOYMENTS_MESSAGE}
                showRefreshButton={false}
                templateType="info"
              />
            </div>
          )}
        </div>
      </div>
      <RoiActionModal
        isOpen={showDeleteModal.isOpen}
        onClose={handleCloseModal}
        onDelete={confirmDeleteRoi}
        selectedRoi={showDeleteModal.selectedRoi}
        appName={cvAppName}
        imageSize={imageSize}
        snapshot={floorImage}
        title={ROI_MESSAGES.DELETE_ROI}
        bodyMessage={ROI_MESSAGES.DELETE_CONFIRMATION_TEXT}
        bodyMessageContinue={ROI_MESSAGES.DELETE_CONFIRMATION_TEXT_CONTINUE}
        confirmButtonText={DELETE_GROUP_MODAL_CONSTANTS.CONFIRM_BUTTON}
        modalType={ROI_MESSAGES.MODAL_TYPE}
      />

      <RoiActionModal
        isOpen={showReDeployModal.isOpen}
        onClose={() => setShowReDeployModal({ isOpen: false, selectedRoi: null })}
        onDelete={handleConfirmReDeploy}
        selectedRoi={showReDeployModal.selectedRoi}
        appName={cvAppName}
        title={DEPLOY_MODAL_MESSAGES.RE_DEPLOY}
        bodyMessage={DEPLOY_MODAL_MESSAGES.CONFIRM_BODY_TEXT}
        bodyMessageContinue={DEPLOY_MODAL_MESSAGES.CONFIRM_BODY_TEXT_CONTINUE}
        confirmButtonText={DEPLOY_MODAL_MESSAGES.CONFIRM_BUTTON}
        modalType={DEPLOY_MODAL_MESSAGES.MODAL_TYPE}
      />
    </div>
  );
};

export default AssociatedStreams;